import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './CensusChoice.css';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { AnimatePresence, motion } from 'framer-motion';
import { jsPDF } from 'jspdf';
import { AxiosError } from 'axios';

interface Polo {
  id_acting: number;
  state: string;
  city: string;
  polo: string;
  address: string;
  admin_name: string;
}

interface PersonalData {
  id_personal_data: number;
  id_user: number;
  name: string;
  sex: string;
  race: string;
  social_name: string;
  marital_status: string;
  date_of_birth: string;
  residential_phone: string;
  cell_phone: string;
  workplace_phone: string;
  current_capacity: string;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

/** ======= CONFIG DA LOGO EM /public ======= */
const LOGO_FILE = 'censo g2_page-0001.jpg';

/** Carrega imagem do /public como DataURL (para PDF) */
const loadPublicImageAsDataURL = async (fileName: string) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const url = encodeURI(`${base}/${fileName}`);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Falha ao carregar imagem: ${url}`);

  const blob = await res.blob();
  const dataURL = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const dim = await new Promise<{ w: number; h: number }>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataURL;
  });

  return { dataURL, ...dim };
};

/** Monta URL segura do arquivo no /public (para janela de impressão) */
const publicUrl = (fileName: string) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  return encodeURI(`${base}/${fileName}`);
};

const CensusChoice: React.FC = () => {
  const { token, id_user, email } = useAuth();
  const navigate = useNavigate();
  const [showPresencial, setShowPresencial] = useState(false);
  const [polos, setPolos] = useState<Polo[]>([]);
  const [personalData, setPersonalData] = useState<PersonalData | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedPolo, setSelectedPolo] = useState<Polo | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [confirmation, setConfirmation] = useState<{ date: string; time: string; token: string } | null>(null);

  // Slots 08:00 - 13:30
  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  ];

  const generateToken = () => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10).replace(/-/g, '');
    const randomDigits = Math.floor(10000 + Math.random() * 90000);
    return `CENSO-${date}-${randomDigits}`;
  };

  const formatBrazilianDate = (dateString: string): string => {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // >>> busca que permite 404/"not found" sem travar o fluxo <<<
  useEffect(() => {
    const fetchPersonalData = async () => {
      if (!token || !id_user) {
        setErrorMessage('Sessão expirada. Faça login novamente.');
        navigate('/rj');
        return;
      }

      try {
        const response = await api.get(`/personaldata/${id_user}`, {
          validateStatus: () => true,
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200 && response.data?.message === 'Personal data retrieved') {
          setPersonalData(response.data.data);
          setErrorMessage(null);
        } else if (
          response.status === 404 ||
          response.data?.message === 'Personal data not found'
        ) {
          setPersonalData(null);
          setErrorMessage(null);
        } else {
          throw new Error(response.data?.message || 'Resposta inválida ao buscar dados pessoais.');
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao buscar dados pessoais:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Erro ao buscar dados pessoais.');
      }
    };

    fetchPersonalData();
  }, [token, id_user, navigate]);

  // >>> se "not found", navega para criar do zero <<<
  const handleOnlineCensus = async () => {
    if (!token || !id_user) {
      setErrorMessage('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }

    try {
      const res = await api.get(`/personaldata/${id_user}`, {
        validateStatus: () => true,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 200 && res.data?.message === 'Personal data retrieved') {
        navigate('/rj/personal-data', { state: { existingData: res.data.data, type: 'online', createNew: false } });
      } else if (res.status === 404 || res.data?.message === 'Personal data not found') {
        navigate('/rj/personal-data', { state: { existingData: null, type: 'online', createNew: true } });
      } else {
        throw new Error(res.data?.message || 'Erro ao verificar dados do censo.');
      }
    } catch (error) {
      console.error('Erro ao verificar dados do censo:', error);
      setErrorMessage('Erro ao verificar dados do censo. Tente novamente.');
    }
  };

  const handlePresencialCensus = async () => {
    if (!token || !id_user) {
      setErrorMessage('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.get('/acting', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.message === 'Acting retrieved') {
        setPolos(response.data.data || []);
        setShowPresencial(true);
      } else {
        throw new Error('Resposta inválida ao buscar polos de atuação.');
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro ao buscar polos:', error.response?.status, error.response?.data);
      setErrorMessage(error.response?.data?.message || 'Erro ao buscar dados do censo presencial.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToChoice = () => {
    setShowPresencial(false);
    setPolos([]);
    setSelectedState('');
    setSelectedCity('');
    setSelectedPolo(null);
    setSelectedDate(null);
    setSelectedTime('');
    setConfirmation(null);
    setErrorMessage(null);
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime || !selectedPolo || !personalData || !email) {
      setErrorMessage('Selecione data, hora, polo e verifique se os dados pessoais estão carregados.');
      return;
    }

    if (!token || !id_user) {
      setErrorMessage('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }

    const generatedToken = generateToken();
    const consultation_date = selectedDate.toISOString().split('T')[0];
    const time = `${selectedTime}:00`;

    setLoading(true);
    setErrorMessage(null);
    try {
      const scheduleResponse = await api.post('/censoinperson', {
        consultation_date,
        time,
        token: generatedToken,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (scheduleResponse.status !== 200 && scheduleResponse.status !== 201) {
        throw new Error('Erro ao agendar o censo presencial.');
      }

      const emailHtml = `
        <h1>Confirmação de Agendamento - Censo Prev</h1>
        <p><strong>Nome:</strong> ${personalData.name}</p>
        <p><strong>Token:</strong> ${generatedToken}</p>
        <p><strong>Data:</strong> ${formatBrazilianDate(consultation_date)}</p>
        <p><strong>Hora:</strong> ${selectedTime}</p>
        <p><strong>Endereço:</strong> ${selectedPolo.address}, ${selectedPolo.city} - ${selectedPolo.state}</p>
        <p><strong>Responsável:</strong> ${selectedPolo.admin_name}</p>
        <p>Este documento foi gerado automaticamente.</p>
      `;

      await api.post('/email/send', {
        to: email,
        subject: 'Confirmação de Agendamento - Censo Prev',
        text: `Agendamento confirmado para ${personalData.name}. Token: ${generatedToken}, Data: ${formatBrazilianDate(consultation_date)}, Hora: ${selectedTime}, Endereço: ${selectedPolo.address}, ${selectedPolo.city} - ${selectedPolo.state}, Responsável: ${selectedPolo.admin_name}`,
        html: emailHtml,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setConfirmation({ date: consultation_date, time: selectedTime, token: generatedToken });
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro ao processar agendamento ou envio de e-mail:', error.response?.status, error.response?.data);
      setErrorMessage(error.response?.data?.message || error.response?.data?.error || 'Erro ao agendar o censo presencial ou enviar o e-mail de confirmação.');
    } finally {
      setLoading(false);
    }
  };

  // Reset encadeado
  useEffect(() => {
    setSelectedCity('');
    setSelectedPolo(null);
    setSelectedDate(null);
    setSelectedTime('');
    setConfirmation(null);
  }, [selectedState]);

  useEffect(() => {
    setSelectedPolo(null);
    setSelectedDate(null);
    setSelectedTime('');
    setConfirmation(null);
  }, [selectedCity]);

  useEffect(() => {
    setSelectedDate(null);
    setSelectedTime('');
    setConfirmation(null);
  }, [selectedPolo]);

  const uniqueStates = polos
    .map((p) => p.state)
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort();

  const uniqueCities = polos
    .filter((p) => p.state === selectedState)
    .map((p) => p.city)
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort();

  const availablePolos = polos
    .filter((p) => p.state === selectedState && p.city === selectedCity)
    .sort((a, b) => a.polo.localeCompare(b.polo));

  const daysOfWeek = ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'];

  const isSameMonth = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return isSameMonth(date1, date2) && date1.getDate() === date2.getDate();
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getCalendarDays = () => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days = [];
    let day = new Date(startDate);
    while (day <= endDate) {
      days.push(new Date(day));
      day.setDate(day.getDate() + 1);
    }
    return days;
  };

  const handleDateClick = (day: Date) => {
    if (!isWeekend(day) && isSameMonth(day, currentMonth)) {
      setSelectedDate(day);
      setSelectedTime('');
    }
  };

  const monthName = currentMonth
    .toLocaleString('pt-BR', { month: 'long', year: 'numeric' })
    .replace(/^\w/, (c) => c.toUpperCase());

  // ---------- PDF com logo no topo ----------
  const downloadPDF = async () => {
    if (!confirmation || !personalData || !selectedPolo) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    let yStart = 15;
    try {
      const { dataURL, w, h } = await loadPublicImageAsDataURL(LOGO_FILE);
      const logoWidth = Math.min(60, pageWidth - 40); // 60mm max
      const logoHeight = (h / w) * logoWidth;
      const x = (pageWidth - logoWidth) / 2;
      const y = 10;

      const imgType = dataURL.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(dataURL, imgType as any, x, y, logoWidth, logoHeight);
      yStart = y + logoHeight + 8;
    } catch (e) {
      console.warn('Logo não carregada no PDF, seguindo sem:', e);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    doc.text('Comprovante de Agendamento', 20, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Nome:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(personalData.name, 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Token:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(confirmation.token, 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Data:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(formatBrazilianDate(confirmation.date), 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Hora:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(confirmation.time, 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Endereço:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(`${selectedPolo.address}, ${selectedPolo.city} - ${selectedPolo.state}`, 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Responsável:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(selectedPolo.admin_name, 50, yStart);
    yStart += 12;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Este documento foi gerado automaticamente.', 20, yStart);

    doc.setDrawColor(0, 51, 102);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, doc.internal.pageSize.getWidth() - 20, doc.internal.pageSize.getHeight() - 20, 'S');

    doc.save(`agendamento_${confirmation.token}.pdf`);
  };

  // ---------- Impressão com logo no topo ----------
  const printConfirmation = () => {
    if (!confirmation || !personalData || !selectedPolo) return;

    const logoSrc = publicUrl(LOGO_FILE);

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Imprimir Agendamento</title>
            <style>
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
              body { font-family: Arial, sans-serif; padding: 24px; text-align: center; }
              .logo {
                display: block;
                margin: 0 auto 12px auto;
                width: 140px;
                height: auto;
              }
              .confirmation-container {
                max-width: 420px; margin: 0 auto; padding: 20px;
                border: 2px solid #003366; border-radius: 8px; background-color: #f9f9f9;
                text-align: left;
              }
              h1 { color: #003366; background-color: #c8dcff; padding: 10px;
                   margin: 12px -20px 16px -20px; border-radius: 6px 6px 0 0; text-align: center; }
              h2 { color: #286ea7; text-align: center; margin: 0 0 12px 0; }
              .row { margin: 8px 0; }
              .label { font-weight: bold; display: inline-block; width: 110px; }
              .footer { color: #646464; font-size: 12px; margin-top: 16px; text-align: center; }
            </style>
          </head>
          <body>
            <img class="logo" src="${logoSrc}" alt="Logo Censo Prev" />
            <div class="confirmation-container">
              <h1>Censo Prev</h1>
              <h2>Comprovante de Agendamento</h2>

              <div class="row"><span class="label">Nome:</span><span>${personalData.name}</span></div>
              <div class="row"><span class="label">Token:</span><span>${confirmation.token}</span></div>
              <div class="row"><span class="label">Data:</span><span>${formatBrazilianDate(confirmation.date)}</span></div>
              <div class="row"><span class="label">Hora:</span><span>${confirmation.time}</span></div>
              <div class="row"><span class="label">Endereço:</span><span>${selectedPolo.address}, ${selectedPolo.city} - ${selectedPolo.state}</span></div>
              <div class="row"><span class="label">Responsável:</span><span>${selectedPolo.admin_name}</span></div>

              <p class="footer">Este documento foi gerado automaticamente.</p>
            </div>

            <script>
              const imgs = Array.from(document.images || []);
              Promise.all(
                imgs.map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = img.onerror = res; }))
              ).then(() => {
                window.print();
                window.onafterprint = () => window.close();
              });
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  if (!token || !id_user) {
    return (
      <>
        <TopBar />
        <PageTransition>
          <div className="choice-wrapper">
            <div className="choice-content">
              <h2>Sessão expirada</h2>
              <p>Faça login novamente.</p>
              <button
                onClick={() => navigate('/rj')}
                className="choice-button"
                aria-label="Ir para a página de login"
              >
                Ir para Login
              </button>
            </div>
          </div>
        </PageTransition>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="choice-wrapper">
          <AnimatePresence>
            {errorMessage && <Alert variant="error" className="choice-alert">{errorMessage}</Alert>}
          </AnimatePresence>
          {loading && !showPresencial && (
            <p className="choice-loading">
              <Spinner /> Carregando...
            </p>
          )}
          <AnimatePresence mode="wait">
            {!showPresencial && (
              <motion.div
                key="choice"
                className="choice-content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
              >
                <h2>Escolha o tipo de censo</h2>
                {personalData ? (
                  <p>Bem-vindo, {personalData.name}!</p>
                ) : (
                  <p>Você ainda não possui dados pessoais cadastrados. Podemos criá-los agora.</p>
                )}
                <div className="choice-cards">
                  <motion.button
                    onClick={handleOnlineCensus}
                    className="choice-card"
                    disabled={loading}
                    aria-label="Iniciar censo online"
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="choice-card__icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="12" rx="2" />
                        <line x1="8" y1="20" x2="16" y2="20" />
                        <line x1="12" y1="16" x2="12" y2="20" />
                      </svg>
                    </span>
                    <span className="choice-card__title">{personalData ? 'Censo Online' : 'Criar Censo Online'}</span>
                    <span className="choice-card__desc">Preencha o formulário pela internet, no seu tempo.</span>
                  </motion.button>
                  <motion.button
                    onClick={handlePresencialCensus}
                    className="choice-card"
                    disabled={loading}
                    aria-label="Iniciar censo presencial"
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="choice-card__icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12z" />
                        <circle cx="12" cy="9" r="2.5" />
                      </svg>
                    </span>
                    <span className="choice-card__title">Censo Presencial</span>
                    <span className="choice-card__desc">Agende um horário em um dos nossos polos.</span>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {showPresencial && (
              <motion.div
                key="presencial"
                className="presencial-wrapper"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
              >
                <div className="presencial-header">
                  <button
                    type="button"
                    className="presencial-back"
                    onClick={handleBackToChoice}
                    aria-label="Voltar para a escolha do tipo de censo"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12" />
                      <polyline points="12 19 5 12 12 5" />
                    </svg>
                    Voltar
                  </button>
                  <h3>Selecione o polo de atuação</h3>
                </div>

                {!confirmation && (
                  <div className="presencial-selectors">
                    <div className="form-group">
                      <label htmlFor="state-select">Estado</label>
                      <select
                        id="state-select"
                        className="form-input"
                        value={selectedState}
                        onChange={(e) => setSelectedState(e.target.value)}
                        disabled={loading}
                        aria-label="Selecionar estado"
                      >
                        <option value="">Selecione um estado</option>
                        {uniqueStates.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>

                    <AnimatePresence>
                      {selectedState && (
                        <motion.div
                          className="form-group"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <label htmlFor="city-select">Cidade</label>
                          <select
                            id="city-select"
                            className="form-input"
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            disabled={loading}
                            aria-label="Selecionar cidade"
                          >
                            <option value="">Selecione uma cidade</option>
                            {uniqueCities.map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                          </select>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {selectedCity && (
                        <motion.div
                          className="form-group"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <label htmlFor="polo-select">Polo</label>
                          <select
                            id="polo-select"
                            className="form-input"
                            value={selectedPolo?.id_acting || ''}
                            onChange={(e) => {
                              const poloId = parseInt(e.target.value);
                              const polo = availablePolos.find((p) => p.id_acting === poloId) || null;
                              setSelectedPolo(polo);
                            }}
                            disabled={loading}
                            aria-label="Selecionar polo de atuação"
                          >
                            <option value="">Selecione um polo</option>
                            {availablePolos.map((polo) => (
                              <option key={polo.id_acting} value={polo.id_acting}>
                                {polo.polo}
                              </option>
                            ))}
                          </select>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                <AnimatePresence>
                  {selectedPolo && (
                    <motion.div
                      className="polo-details"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      <strong>Endereço</strong>
                      <p>
                        {selectedPolo.address}
                        <br />
                        {selectedPolo.city} - {selectedPolo.state}
                      </p>
                      <strong>Responsável</strong>
                      <p>{selectedPolo.admin_name}</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {selectedPolo && !confirmation && (
                    <motion.div
                      className="schedule-group"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      <h4>Agende sua consulta</h4>
                      <div className="calendar">
                        <div className="calendar-month">
                          <button
                            type="button"
                            className="calendar-nav"
                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                            disabled={loading}
                            aria-label="Mês anterior"
                          >
                            ‹
                          </button>
                          <span>{monthName}</span>
                          <button
                            type="button"
                            className="calendar-nav"
                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                            disabled={loading}
                            aria-label="Próximo mês"
                          >
                            ›
                          </button>
                        </div>
                        <div className="days-header">
                          {daysOfWeek.map((day, i) => (
                            <div key={`${day}-${i}`}>{day}</div>
                          ))}
                        </div>
                        <div className="days-grid">
                          {getCalendarDays().map((day, index) => (
                            <button
                              type="button"
                              key={index}
                              className={`day ${!isSameMonth(day, currentMonth) ? 'day--out' : ''} ${
                                isWeekend(day) ? 'day--weekend' : ''
                              } ${selectedDate && isSameDay(day, selectedDate) ? 'day--selected' : ''} ${
                                isSameDay(day, new Date()) ? 'day--today' : ''
                              }`}
                              onClick={() => handleDateClick(day)}
                              disabled={isWeekend(day) || !isSameMonth(day, currentMonth)}
                              aria-label={`Selecionar dia ${day.getDate()}`}
                            >
                              {day.getDate()}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="time-select">Horário</label>
                        <select
                          id="time-select"
                          className="form-input"
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          disabled={!selectedDate || loading}
                          aria-label="Selecionar horário"
                        >
                          <option value="">Selecione um horário</option>
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        onClick={handleSchedule}
                        loading={loading}
                        disabled={loading}
                        className="schedule-submit"
                        aria-label="Confirmar agendamento"
                      >
                        Agendar
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {confirmation && (
                    <motion.div
                      className="confirmation"
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      <span className="confirmation__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <h3>Agendamento confirmado!</h3>
                      {personalData && (
                        <p className="confirmation__row">
                          <strong>Nome:</strong> {personalData.name}
                        </p>
                      )}
                      <p className="confirmation__row">
                        <strong>Data:</strong> {formatBrazilianDate(confirmation.date)}
                      </p>
                      <p className="confirmation__row">
                        <strong>Hora:</strong> {confirmation.time}
                      </p>
                      <p className="confirmation__token">{confirmation.token}</p>
                      <div className="confirmation-actions">
                        <Button
                          variant="ghost"
                          onClick={() => { void downloadPDF(); }}
                          disabled={loading}
                          aria-label="Baixar comprovante de agendamento em PDF"
                        >
                          Baixar PDF
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={printConfirmation}
                          disabled={loading}
                          aria-label="Imprimir comprovante de agendamento"
                        >
                          Imprimir
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PageTransition>
    </>
  );
};

export default CensusChoice;
